import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription, combineLatest, filter } from "rxjs";
import { Device } from "src/app/model/device";
import { AppState } from "src/app/store/app.reducer";
import { selectCurrentDevice, selectCurrentDeviceInfo, selectCurrentDeviceModel, selectShared, selectSharing } from "src/app/store/device/device.selector";
import * as DeviceActions from 'src/app/store/device/device.actions';
import { CustomValidators } from "src/app/validators/custom.validators";
import { DeviceModel } from "src/app/model/device-model";
import { DeviceInfo } from "src/app/model/device-info";
import { StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";

@Component({
	templateUrl: './share-device.dialog.component.html',
})

export class ShareDeviceDialogComponent implements OnInit, OnDestroy {
	shareDeviceForm: FormGroup;

	clientsNum: number[] = [1, 2, 3, 4, 5];

	infoSub: Subscription;

	deviceName: string;
	remoteMax = 5;
	mobileRemotesSupport: boolean;

	sharedSub: Subscription;

	currentDevice$: Observable<Device | undefined>;
	currentDeviceModel$: Observable<DeviceModel | undefined>;
	currentDeviceInfo$: Observable<DeviceInfo | undefined>;
	deviceShared$: Observable<boolean | undefined>;

	deviceSharing: boolean;
	deviceSharing$: Observable<boolean | undefined>;
	deviceSharingSub: Subscription;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private dialogRef: DynamicDialogRef,
		public config: DynamicDialogConfig
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	initSelectors(): void {
		this.currentDevice$ = this.store.select(selectCurrentDevice);
		this.currentDeviceModel$ = this.store.select(selectCurrentDeviceModel);
		this.currentDeviceInfo$ = this.store.select(selectCurrentDeviceInfo);
		this.deviceShared$ = this.store.select(selectShared);
		this.deviceSharing$ = this.store.select(selectSharing);
	}

	subscribeAll(): void {
		this.infoSub = combineLatest([
			this.currentDevice$,
			this.currentDeviceInfo$,
			this.currentDeviceModel$,
		]).subscribe(([device, info, model]) => {
			this.deviceName = device.Name;
			this.remoteMax = info.RemotesMax || 1;
			this.mobileRemotesSupport = model.MobileRemotes ?? false;
			this.buildForm();
		});
		this.sharedSub = this.deviceShared$.pipe(filter(res => !!res))
			.subscribe(() => {
				this.dialogRef.close('success');
			}
		);
		this.deviceSharingSub = this.deviceSharing$?.subscribe(
			(sharing) => {
				this.deviceSharing = sharing;
			}
		);
	}

	ngOnDestroy(): void {
		this.infoSub?.unsubscribe();
		this.sharedSub?.unsubscribe();
		this.deviceSharingSub?.unsubscribe();
	}

	buildForm(): void {
		this.shareDeviceForm = new FormGroup({
			'firstName': new FormControl('', [Validators.required, Validators.maxLength(40)]),
			'lastName': new FormControl('', [Validators.required, Validators.maxLength(40)]),
			'email': new FormControl('', [Validators.required, Validators.email]),
			'emailConfirm': new FormControl('', [Validators.required, Validators.email]),
			'remotesMax': new FormControl(this.remoteMax, Validators.required,)
		});
		this.shareDeviceForm.setValidators(CustomValidators.checkMatchingFields('email', 'emailConfirm'));
		if (!this.mobileRemotesSupport) {
			this.f.remotesMax.disable();
		}
	}

	get f(): any {
		return this.shareDeviceForm.controls;
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.shareDeviceForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.shareDeviceForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const payload = {
			...this.shareDeviceForm.value,
		};
		delete payload.emailConfirm;
		this.store.dispatch(DeviceActions.shareDevice(payload));
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	justifyMessagesNewValidator(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: ShareDeviceDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorsConfirmFields(type: ShareDeviceDialogComponent["f"], classType: string, notEquivalentType: string): string {
		return StandardNewValidator.showErrorsConfirmFields(type, classType, notEquivalentType, this.screenWidth, this.shareDeviceForm);
	}

	showErrorRequiredMessage(type: ShareDeviceDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showErrorConfirmInvalidMessage(type: ShareDeviceDialogComponent["f"], notEquivalentType: string): boolean {
		return StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, this.shareDeviceForm);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}